import { useEffect, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { Department } from "../../api/types";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { formatDate } from "../../lib/format";

type LoadStatus = "loading" | "ready" | "error";

// DepartmentsPage is the leadership CRUD list for the labels positions belong
// to. A department referenced by positions cannot be deleted (409 from the
// server) — the error is surfaced in the confirm modal.
export function DepartmentsPage() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Department | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const createKey = useIdempotencyKey();
  const editKey = useIdempotencyKey();
  const deleteKey = useIdempotencyKey();
  const overlayDismiss = useOverlayDismiss(cancelDelete);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    apiClient
      .listDepartments()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setDepartments(response.departments);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = createName.trim();
    if (name === "" || creating) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await apiClient.createDepartment(name, createKey.keyFor(`create:${name}`));
      setCreateName("");
      createKey.reset();
      setReloadKey((key) => key + 1);
    } catch (caught) {
      setCreateError(messageFor(caught, "Couldn't create department."));
    } finally {
      setCreating(false);
    }
  }

  function startEdit(dept: Department) {
    setEditingId(dept.id);
    setEditName(dept.name);
    setEditError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditName("");
    setEditError(null);
  }

  async function handleEditSubmit(event: React.FormEvent<HTMLFormElement>, dept: Department) {
    event.preventDefault();
    const name = editName.trim();
    if (name === "" || editSubmitting) {
      return;
    }
    if (name === dept.name) {
      cancelEdit();
      return;
    }
    setEditSubmitting(true);
    setEditError(null);
    try {
      await apiClient.updateDepartment(dept.id, name, editKey.keyFor(`rename:${dept.id}:${name}`));
      editKey.reset();
      cancelEdit();
      setReloadKey((key) => key + 1);
    } catch (caught) {
      setEditError(messageFor(caught, "Couldn't rename department."));
    } finally {
      setEditSubmitting(false);
    }
  }

  function requestDelete(dept: Department) {
    setPendingDelete(dept);
    setDeleteError(null);
  }

  function cancelDelete() {
    if (deleting) {
      return;
    }
    setPendingDelete(null);
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (pendingDelete === null || deleting) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiClient.deleteDepartment(pendingDelete.id, deleteKey.keyFor(`delete:${pendingDelete.id}`));
      deleteKey.reset();
      setPendingDelete(null);
      setReloadKey((key) => key + 1);
    } catch (caught) {
      setDeleteError(messageFor(caught, "Couldn't delete department."));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="departments-view">
      <section className="card">
        <h2>Create department</h2>
        <form onSubmit={handleCreate} className="form-row form-row--inline">
          <label>
            Name
            <input
              type="text"
              value={createName}
              maxLength={100}
              onChange={(event) => setCreateName(event.target.value)}
              placeholder="Engineering"
              required
            />
          </label>
          <button type="submit" className="btn btn--primary" disabled={creating || createName.trim() === ""}>
            {creating ? "Creating…" : "Create"}
          </button>
        </form>
        {createError !== null && <p className="form-row__error">{createError}</p>}
      </section>

      <section className="card">
        <h2>Departments</h2>
        {status === "loading" && <p className="loading">Loading…</p>}
        {status === "error" && (
          <p className="form-row__error">
            Couldn't load departments.{" "}
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setReloadKey((key) => key + 1)}>
              Retry
            </button>
          </p>
        )}
        {status === "ready" && departments.length === 0 && (
          <p className="muted">No departments yet — create one above. Positions need a department.</p>
        )}
        {status === "ready" && departments.length > 0 && (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {departments.map((dept) => (
                <tr key={dept.id}>
                  {editingId === dept.id ? (
                    <td colSpan={3}>
                      <form
                        className="form-row form-row--inline"
                        onSubmit={(event) => void handleEditSubmit(event, dept)}
                      >
                        <input
                          type="text"
                          value={editName}
                          maxLength={100}
                          onChange={(event) => setEditName(event.target.value)}
                          aria-label="Department name"
                          autoFocus
                        />
                        <button type="button" className="btn btn--ghost btn--sm" onClick={cancelEdit} disabled={editSubmitting}>
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn--primary btn--sm"
                          disabled={editName.trim() === "" || editSubmitting}
                        >
                          {editSubmitting ? "Saving…" : "Save"}
                        </button>
                        {editError !== null && <p className="form-row__error">{editError}</p>}
                      </form>
                    </td>
                  ) : (
                    <>
                      <td>{dept.name}</td>
                      <td>{formatDate(dept.created_at)}</td>
                      <td className="row-actions">
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => startEdit(dept)}>
                          Rename
                        </button>
                        <button type="button" className="btn btn--danger btn--sm" onClick={() => requestDelete(dept)}>
                          Delete
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {pendingDelete !== null && (
        <div className="modal-overlay" role="presentation" {...overlayDismiss}>
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-department-title"
          >
            <header className="modal__header">
              <h2 id="delete-department-title">Delete {pendingDelete.name}?</h2>
              <button type="button" className="modal__close" aria-label="Close" onClick={cancelDelete} disabled={deleting}>
                ×
              </button>
            </header>
            <div className="modal__body">
              <p>A department that positions still reference cannot be deleted — reassign or delete those positions first.</p>
              {deleteError !== null && <p className="form-row__error">{deleteError}</p>}
              <div className="modal__footer">
                <button type="button" className="btn btn--ghost" onClick={cancelDelete} disabled={deleting}>
                  Cancel
                </button>
                <button type="button" className="btn btn--danger" onClick={() => void confirmDelete()} disabled={deleting}>
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function messageFor(caught: unknown, fallback: string): string {
  if (caught instanceof ApiError) {
    if (caught.code === "department_in_use") {
      return "Positions still reference this department — reassign or delete them first.";
    }
    if (caught.code === "department_exists") {
      return "A department with that name already exists.";
    }
    return caught.message;
  }
  return caught instanceof Error ? caught.message : fallback;
}
